import React from 'react';

import {
  BillingCycle,
  GetBillingDetailsQuery,
  GetPlansQuery,
  PlanOptionFragment,
} from '~/graphql/types';

import Catalog from '~/Catalog';
import { Coupon } from '~/components/page/Settings/Subscription/utils/convertToCoupon';
import { isNil } from 'ramda';
import { ApolloError } from '@apollo/client';
import CancelSubscriptionStep from '../../steps/CancelSubscriptionStep';
import ChoosePlanStep from '../../steps/ChoosePlanStep';
import PlanOverviewStep from '../../steps/PlanOverviewStep';
import SetupPayment from '../../steps/SetupPayment';
import type { StepId } from '../..';
import NoResultSection from '~/components/page/NoResultSection';
import type { RefetchPlansQuery } from '../../types';
import useSessionHydration from '~/hooks/useSessionHydration';

const text = {
  insertionErrorMsg: Catalog.genericUnknownErrorMessageShort,
  header: 'Kies je abonnement',
  noPlansAvailable: 'Geen abonnementen beschikbaar',
};

type Props = {
  /** Setup has been completed */
  accountIsSetup: boolean;
  currentStep: StepId;
  plansData?: GetPlansQuery['getPlans'];
  billingDetailsData?: GetBillingDetailsQuery;
  couponCode: string | null | undefined;
  billingInfo: {
    name: string | null;
    email: string | null;
  };
  mutationLoading: boolean;
  mutationError: ApolloError | undefined;
  updateData: {
    plan: null | PlanOptionFragment;
    billingCycle: BillingCycle;
    coupon: null | Coupon;
  };
  setUpdateData: React.Dispatch<
    React.SetStateAction<{
      plan: null | PlanOptionFragment;
      billingCycle: BillingCycle;
      coupon: null | Coupon;
    }>
  >;
  setBillingInfo: React.Dispatch<
    React.SetStateAction<{
      name: string | null;
      email: string | null;
    }>
  >;
  setCurrentStep: React.Dispatch<React.SetStateAction<StepId>>;
  onConfirm: ({
    planId,
    paymentId,
    billingCycle,
    coupon,
    shouldUseUpdate,
  }: {
    planId: string;
    paymentId?: string | undefined;
    billingCycle: BillingCycle;
    coupon: null | Coupon;
    shouldUseUpdate?: boolean | undefined;
  }) => Promise<void>;
  onComplete: () => void;
  onClose?: () => void;
  refetchPlans: RefetchPlansQuery;
};

const StepComponent: React.FC<Props> = ({
  accountIsSetup,
  mutationError,
  mutationLoading,

  updateData,
  currentStep,
  plansData,
  billingDetailsData,
  billingInfo,
  couponCode,

  setBillingInfo,
  setUpdateData,
  setCurrentStep,

  onConfirm,
  onClose,
  onComplete,
  refetchPlans,
}) => {
  const [{ impersonatedBy }] = useSessionHydration();

  if (!plansData)
    return <NoResultSection>{text.noPlansAvailable}</NoResultSection>;

  switch (currentStep) {
    case 'choosePlan':
      return (
        <ChoosePlanStep
          header={text.header}
          currentPlan={plansData.current}
          planOptions={plansData.available}
          caveats={plansData.caveats}
          onContinue={(plan, billingCycle) => {
            setUpdateData(prev => ({
              ...prev,
              billingCycle,
              plan,
            }));
            setCurrentStep('planOverview');
          }}
          onClose={onClose}
          onGoToUnsubscribe={
            !isNil(plansData.current) && !isNil(impersonatedBy)
              ? () => {
                  setCurrentStep('cancelSubscription');
                }
              : null
          }
          refetchPlans={refetchPlans}
        />
      );

    case 'planOverview':
      return (
        <PlanOverviewStep
          accountIsSetup={accountIsSetup}
          selectedPlan={updateData.plan as PlanOptionFragment}
          initialSelectedBillingCycle={updateData.billingCycle}
          initialSelectedCoupon={updateData.coupon}
          initialCouponCode={couponCode}
          onContinue={(billingCycle, coupon) => {
            const currentPlan = updateData.plan;

            setUpdateData(prev => ({
              ...prev,
              coupon: coupon,
              billingCycle,
            }));

            if (
              updateData.plan?.requiresBilling === true &&
              isNil(billingDetailsData?.getBillingDetails)
            ) {
              return setCurrentStep('setupPayment');
            }

            if (currentPlan) {
              return onConfirm({
                planId: currentPlan.id,
                billingCycle,
                coupon,
                shouldUseUpdate: !isNil(plansData.current),
              });
            }
          }}
          onGoBack={(billingCycle, coupon) => {
            setUpdateData(prev => ({
              ...prev,
              coupon,
              billingCycle,
            }));

            setCurrentStep('choosePlan');
          }}
          loading={mutationLoading}
          hasBillingDetails={!isNil(billingDetailsData?.getBillingDetails)}
        />
      );
    case 'setupPayment': {
      const price = updateData.plan?.price[updateData.billingCycle] ?? 0;
      const totalPrice =
        updateData.billingCycle === BillingCycle.Monthly ? price : price * 12;

      return (
        <SetupPayment
          onContinue={(paymentId: string) => {
            if (updateData.plan) {
              return onConfirm({
                planId: updateData.plan.id,
                billingCycle: updateData.billingCycle,
                coupon: updateData.coupon,
                paymentId,
                shouldUseUpdate: !isNil(plansData.current),
              });
            }

            return;
          }}
          onGoBack={billingInfo => {
            setBillingInfo(billingInfo);
            setCurrentStep('planOverview');
          }}
          totalPriceToPay={
            totalPrice -
            (updateData.coupon != null
              ? updateData.coupon.calculateOff(totalPrice)
              : 0)
          }
          insertionLoading={mutationLoading}
          insertionError={mutationError == null ? null : text.insertionErrorMsg}
          initialEmail={billingInfo.email}
          initialName={billingInfo.name}
        />
      );
    }

    case 'cancelSubscription':
      return (
        <CancelSubscriptionStep
          onGoBack={() => setCurrentStep('choosePlan')}
          onSubscriptionCancelled={() => {
            onComplete();
          }}
        />
      );
  }
};

export default StepComponent;
